import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
} from "@mui/material";

import { Loading } from "@lopie/kernel-ui";
import {
  RemoteError,
  loadRemote,
  mountContext,
  teardown,
} from "@lopie/kernel-ui/host";

/**
 * One mounted micro-app, and the four ways that goes wrong.
 *
 * The host half of `docs/remote-contract.md`. What is worth reading here is the
 * effect, because a portal breaks in exactly two places and both of them are in
 * it: mounting twice, and failing to unmount.
 *
 * ## React 19 StrictMode double-invokes effects
 *
 * In development every effect runs, is cleaned up, and runs again. A naive
 * implementation calls `mount()` twice and `unmount()` once, which is not the
 * contract and leaves the remote's second copy holding the element. So the
 * load is *cancellable*: the first pass sets `cancelled` before its import
 * resolves, sees it, and returns without mounting. Exactly one `mount` per
 * open, in development and in production, which is what the contract promises.
 *
 * ## Cleanup has to be safe at four different moments
 *
 * Before the import resolves, after it failed, after `mount` threw, and after a
 * clean mount. `mounted` is only set once a mount has actually succeeded, so
 * the teardown never calls `unmount` on a module that was never up.
 *
 * ## Why colour scheme is not in the dependency list
 *
 * It changes when somebody clicks the theme toggle, and a remote that
 * remounted on a theme toggle would lose whatever the user was doing. So the
 * scheme reaches the remote two ways instead: `context.colorScheme` at mount
 * time, and a `data-color-scheme` attribute the host keeps current. A remote
 * that styles off the attribute follows the console live and never remounts.
 */
export default function RemoteMount({
  instance,
  colorScheme,
  signOut,
  onMeta,
}) {
  const hostRef = useRef(null);
  const liveRef = useRef({ colorScheme, signOut });
  const [state, setState] = useState({ phase: "loading" });

  // Kept in a ref so the mount effect can read the current values without
  // taking a dependency on them — and written in an effect rather than during
  // render, because a ref mutated during render is a side effect React is
  // allowed to run twice or throw away.
  //
  // Both of these are declared *before* the mount effect on purpose: effects
  // run in declaration order, so by the time a remote is handed the element,
  // the attribute is already right and the ref already holds this render's
  // props.
  useEffect(() => {
    liveRef.current = { colorScheme, signOut };
  });

  useEffect(() => {
    if (hostRef.current) hostRef.current.dataset.colorScheme = colorScheme;
  }, [colorScheme]);

  const { client_id: clientId, app_type: appType, url } = instance;

  useEffect(() => {
    const element = hostRef.current;
    if (!element) return undefined;

    let cancelled = false;
    let mounted = null;
    setState({ phase: "loading" });

    (async () => {
      const module = await loadRemote(url);
      if (cancelled) return;

      try {
        await module.mount(
          element,
          mountContext({
            instance: clientId,
            appType,
            colorScheme: liveRef.current.colorScheme,
            signOut: liveRef.current.signOut,
          }),
        );
      } catch (cause) {
        // A mount that threw may have left half a UI behind. Clearing it is
        // the difference between "this app failed to start" and "this app
        // failed to start, and here is some of it anyway".
        teardown(module, element);
        throw new RemoteError(
          "mount",
          cause?.message || "the app threw while starting up",
          cause,
        );
      }

      if (cancelled) {
        // The StrictMode window, and a genuine fast navigation. The mount
        // succeeded after the user left, so it is undone rather than left
        // running in a detached element.
        teardown(module, element);
        return;
      }

      mounted = module;
      setState({ phase: "up" });
      onMeta?.(module.meta || null);
    })().catch((error) => {
      if (cancelled) return;
      setState({ phase: "failed", error });
      onMeta?.(null);
    });

    return () => {
      cancelled = true;
      if (mounted) teardown(mounted, element);
      mounted = null;
    };
    // `onMeta` is deliberately absent: it is a setState wrapper from the
    // parent and including it would remount the app on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, appType, url]);

  return (
    <Box>
      {state.phase === "loading" ? (
        <Loading label="Loading this app from its own origin…" />
      ) : null}

      {state.phase === "failed" ? (
        <Failure error={state.error} url={url} />
      ) : null}

      {/*
        Always in the document, never conditionally rendered. The ref has to be
        a real element before the effect runs, and a remote that mounted into a
        node React was about to replace would vanish on the next render.

        `isolation: isolate` gives the remote its own stacking context, so a
        z-index it picked cannot land on top of the console's app bar.
      */}
      <Box
        ref={hostRef}
        // Not aria-hidden while loading: the element is empty, so there is
        // nothing to hide, and toggling it would move focus around under a
        // keyboard user for no gain.
        sx={{
          isolation: "isolate",
          minWidth: 0,
          // The one layout rule the shell enforces on a guest. Wide content
          // scrolls in its own container; the page body never scrolls
          // sideways, whatever the remote does inside.
          overflowX: "auto",
        }}
      />
    </Box>
  );
}

/**
 * Which of the three failures this was, in a sentence somebody can act on.
 *
 * A portal that says "could not load app" for all of them makes a reader open
 * devtools to find out whether the problem is theirs, the remote author's, or
 * the provisioner's.
 */
function Failure({ error, url }) {
  const phase = error instanceof RemoteError ? error.phase : "load";

  const said = {
    load: {
      title: "This app did not load",
      body: (
        <>
          Nothing came back from the app. That is almost always one of three
          things: its address is wrong, the file is not there, or it is served
          without{" "}
          <code>Access-Control-Allow-Origin</code> — the console imports it
          across origins, and module scripts are always fetched in CORS mode.
          <br />
          <br />
          Kernel does not check any of that. It keeps the address it was given
          when the app was installed and says nothing about whether anything is
          at the other end — whether an app is healthy is not something Kernel
          knows.
        </>
      ),
    },
    contract: {
      title: "That URL is not a micro-app",
      body: (
        <>
          The module loaded and exports no <code>mount()</code> function, so
          there is nothing for the console to start. A remote exports{" "}
          <code>mount</code>, <code>unmount</code> and <code>meta</code> — the
          contract is <code>docs/remote-contract.md</code> in the Lopie One
          repo.
          {/* Named, not linked. A link to a repo the reader may not be able
              to open is worse than a path they can search for, and a URL
              invented here would be wrong the first time the forge moves. */}
        </>
      ),
    },
    mount: {
      title: "This app failed to start",
      body: (
        <>
          It loaded and threw while mounting, so this is a fault inside the app
          rather than in the portal or the URL. Its own message is below.
        </>
      ),
    },
  }[phase];

  return (
    <Alert severity="error" variant="outlined">
      <AlertTitle>{said.title}</AlertTitle>
      <Typography variant="body2" component="div">
        {said.body}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 1.5,
          fontFamily: "monospace",
          // A bundle URL and a stack message are both long and neither may
          // push the page sideways.
          overflowWrap: "anywhere",
        }}
      >
        {phase === "mount" ? error.message : url}
      </Typography>
    </Alert>
  );
}
